/**
 * useLocalStorage Hook
 *
 * Generic hook for syncing state with localStorage
 * Automatically handles JSON serialization/deserialization
 *
 * Features:
 * - Cross-component sync: Updates propagate to all hook instances using the same key
 * - Cross-tab sync: Listens for storage events from other browser tabs
 * - Type-safe: Generic type parameter for stored values
 */

import { useState, useCallback, useEffect } from 'react';
import CONFIG from '../config';

/** Custom event name for same-tab localStorage sync */
const LOCAL_STORAGE_CHANGE_EVENT = 'local-storage-change';

/** Custom event detail interface */
interface LocalStorageChangeDetail {
  key: string;
  newValue: string | null;
}

/**
 * Read initial value from localStorage, persisting default if not present
 */
function getStoredValue<T>(key: string, initialValue: T): T {
  try {
    const item = window.localStorage.getItem(key);
    if (item) {
      return JSON.parse(item);
    }
    // Persist initial value to localStorage so it survives page refresh
    try {
      window.localStorage.setItem(key, JSON.stringify(initialValue));
    } catch {
      // Ignore storage errors during initialization
    }
    return initialValue;
  } catch (error) {
    if (CONFIG.DEBUG) {
      console.error(`Error reading localStorage key "${key}":`, error);
    }
    return initialValue;
  }
}

/**
 * Dispatch custom event for same-tab synchronization
 * (Native storage event only fires for other tabs)
 */
function dispatchLocalStorageChange(key: string, newValue: string | null) {
  window.dispatchEvent(
    new CustomEvent<LocalStorageChangeDetail>(LOCAL_STORAGE_CHANGE_EVENT, {
      detail: { key, newValue },
    }),
  );
}

export function useLocalStorage<T>(
  key: string,
  initialValue: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  // Get initial value from localStorage or use provided initialValue
  const [storedValue, setStoredValue] = useState<T>(() =>
    getStoredValue(key, initialValue),
  );

  // Listen for storage changes from other tabs and other components
  useEffect(() => {
    // Handler for cross-tab storage events
    const handleStorageEvent = (event: StorageEvent) => {
      if (event.key !== key || event.storageArea !== localStorage) {
        return;
      }

      try {
        const newValue =
          event.newValue !== null ? JSON.parse(event.newValue) : initialValue;
        setStoredValue(newValue);
      } catch (error) {
        if (CONFIG.DEBUG) {
          console.error(`Error parsing storage event for "${key}":`, error);
        }
      }
    };

    // Handler for same-tab custom events (from other components)
    const handleLocalStorageChange = (
      event: CustomEvent<LocalStorageChangeDetail>,
    ) => {
      if (event.detail.key !== key) {
        return;
      }

      try {
        const newValue =
          event.detail.newValue !== null
            ? JSON.parse(event.detail.newValue)
            : initialValue;
        setStoredValue(newValue);
      } catch (error) {
        if (CONFIG.DEBUG) {
          console.error(`Error parsing custom event for "${key}":`, error);
        }
      }
    };

    // Listen for cross-tab changes
    window.addEventListener('storage', handleStorageEvent);

    // Listen for same-tab changes from other components
    window.addEventListener(
      LOCAL_STORAGE_CHANGE_EVENT,
      handleLocalStorageChange as EventListener,
    );

    return () => {
      window.removeEventListener('storage', handleStorageEvent);
      window.removeEventListener(
        LOCAL_STORAGE_CHANGE_EVENT,
        handleLocalStorageChange as EventListener,
      );
    };
  }, [key, initialValue]);

  // Wrapper for setValue that supports functional updates
  // Writes to localStorage and dispatches sync event in one operation
  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      setStoredValue((prev) => {
        try {
          // Allow value to be a function (same API as useState)
          const newValue = value instanceof Function ? value(prev) : value;

          // Persist to localStorage and notify other components
          try {
            const serialized = JSON.stringify(newValue);
            window.localStorage.setItem(key, serialized);
            dispatchLocalStorageChange(key, serialized);
          } catch (storageError) {
            if (CONFIG.DEBUG) {
              console.error(
                `Error setting localStorage key "${key}":`,
                storageError,
              );
            }
          }

          return newValue;
        } catch (error) {
          if (CONFIG.DEBUG) {
            console.error(`Error updating localStorage key "${key}":`, error);
          }
          return prev;
        }
      });
    },
    [key],
  );

  return [storedValue, setValue];
}
